import { apiClient } from './client';
import type { DynamicSegmentInfo, LkSegmentInfo, SegmentInfo } from './manualBroadcasts';

export interface BroadcastScheduleDateGuard {
  strategy: 'same_day';
  source_fields: string[];
  timezone?: string;
}

export interface BroadcastScheduleConfig {
  template_id: string;
  template_name?: string;
  segment_type: 'regular' | 'dynamic' | 'lk';
  segment_id: number;
  segment_name: string;
  channel_override?: string;
  variable_mapping?: Record<string, { type: string; field?: string; value?: string }>;
  broadcast_group?: string;
  okdoc_scenario_profile?: string;
  date_guard?: BroadcastScheduleDateGuard;
  stagger_minutes?: number;
  ttl_minutes?: number;
}

export interface BroadcastSchedule {
  id: string;
  tenant_id: string;
  name: string;
  campaign_type: string;
  schedule_type: 'daily' | 'weekly' | 'cron';
  cron_expr?: string;
  timezone: string;
  send_time: string;
  is_active: boolean;
  launch_locked: boolean;
  last_run_at?: string;
  next_run_at?: string;
  config: BroadcastScheduleConfig;
  created_at: string;
  updated_at: string;
}

export interface BroadcastScheduleRun {
  id: string;
  schedule_id: string;
  tenant_id: string;
  trigger_source: string;
  status: string;
  audience_total: number;
  materialized_count: number;
  queued_count: number;
  sent_count: number;
  failed_count: number;
  skipped_count: number;
  error?: string;
  started_at: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateBroadcastScheduleRequest {
  name: string;
  campaign_type?: string;
  schedule_type: 'daily' | 'weekly' | 'cron';
  cron_expr?: string;
  timezone: string;
  send_time: string;
  is_active: boolean;
  config: BroadcastScheduleConfig;
}

export interface UpdateBroadcastScheduleRequest extends Partial<CreateBroadcastScheduleRequest> {}

export const broadcastSchedulesApi = {
  list: async (params?: { tenant_id?: string; is_active?: boolean; limit?: number; offset?: number }): Promise<{ schedules: BroadcastSchedule[]; total: number }> => {
    const response = await apiClient.get('/broadcast-schedules', { params });
    return {
      schedules: response.data?.schedules ?? [],
      total: response.data?.total ?? 0,
    };
  },

  get: async (id: string, tenantId?: string): Promise<BroadcastSchedule> => {
    const response = await apiClient.get(`/broadcast-schedules/${id}`, {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    });
    return response.data;
  },

  create: async (data: CreateBroadcastScheduleRequest, tenantId?: string): Promise<BroadcastSchedule> => {
    const response = await apiClient.post('/broadcast-schedules', data, {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    });
    return response.data;
  },

  update: async (id: string, data: UpdateBroadcastScheduleRequest, tenantId?: string): Promise<BroadcastSchedule> => {
    const response = await apiClient.put(`/broadcast-schedules/${id}`, data, {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    });
    return response.data;
  },

  remove: async (id: string, tenantId?: string): Promise<void> => {
    await apiClient.delete(`/broadcast-schedules/${id}`, {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    });
  },

  listRuns: async (id: string, tenantId?: string): Promise<{ runs: BroadcastScheduleRun[]; total: number }> => {
    const response = await apiClient.get(`/broadcast-schedules/${id}/runs`, {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    });
    return {
      runs: response.data?.runs ?? [],
      total: response.data?.total ?? 0,
    };
  },

  setLaunchLocked: async (id: string, launchLocked: boolean, tenantId?: string): Promise<{ id: string; launch_locked: boolean }> => {
    const response = await apiClient.post(`/broadcast-schedules/${id}/launch-lock`, { launch_locked: launchLocked }, {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    });
    return response.data;
  },

  listSegments: async (tenantId?: string): Promise<SegmentInfo[]> => {
    const response = await apiClient.get('/manual-broadcasts/segments', {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    });
    const data = response.data;
    return Array.isArray(data) ? data : (data?.segments ?? []);
  },

  listDynamicSegments: async (tenantId?: string): Promise<DynamicSegmentInfo[]> => {
    const response = await apiClient.get('/manual-broadcasts/dynamic-segments', {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    });
    const data = response.data;
    return Array.isArray(data) ? data : (data?.segments ?? []);
  },

  listLkSegments: async (tenantId?: string): Promise<LkSegmentInfo[]> => {
    const response = await apiClient.get('/manual-broadcasts/lk-segments', {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    });
    const data = response.data;
    return Array.isArray(data) ? data : (data?.segments ?? []);
  },
};
